import React, { useEffect, type DependencyList } from 'react';
import { Tooltip } from '_atoms';
import type { TableColumnType } from '_atoms/Table/types';
import { COLLAPSED_BY_NAME, REJECTED_COLLAPSED, HIDE_PERCENTAGE, COLUMNS } from './constants';

export interface ReportingPeriod {
  month: string;
  year: number | string;
  confirmed_on_transition?: number;
  rate?: number;
}

export interface ReportingTransition {
  name: string;
  transition_total?: number;
  periods: ReportingPeriod[];
  tooltip?: string;
}

export interface ReportingSuccessRate {
  average_success_rate: number;
  periods: Array<ReportingPeriod & { rate: number }>;
}

export interface ReportingSectionResponse {
  block_name: string;
  block_total?: number;
  transitions: ReportingTransition[];
  success_rate?: ReportingSuccessRate;
}

export interface RatioCell {
  tooltip: string;
  value: number;
  percentage: string;
}

export interface SuccessRateCell {
  text: string;
  tooltip: string;
}

export type ReportingCellValue = string | number | RatioCell | SuccessRateCell;

export interface DeclinedRow {
  uuid: string;
  name: string;
  total: string | number;
  [key: string]: unknown;
}

export interface TransitionRow {
  uuid: string;
  name: string;
  total: ReportingCellValue;
  declined: DeclinedRow[];
  tooltip?: string;
  [key: string]: unknown;
}

export interface HiringStatisticsRow {
  uuid: string;
  name: string;
  total: string | number;
  transitions: TransitionRow[] | false;
  [key: string]: unknown;
}

export interface SuccessRateRow {
  id: string;
  name: string;
  total: SuccessRateCell;
  title?: boolean;
  uuid?: string;
  [key: string]: unknown;
}

export function useSyncScroll(selector = '.ant-table-body, .ant-table-content', deps: DependencyList = []) {
  useEffect(() => {
    const containers = Array.from(document.querySelectorAll<HTMLElement>(selector));

    if (!containers.length) return undefined;

    let isSyncing = false;

    const handleScroll = (e: Event) => {
      if (isSyncing) return;
      isSyncing = true;

      const { scrollLeft } = e.target as HTMLElement;
      containers.forEach(el => {
        if (el !== e.target) {
          el.scrollLeft = scrollLeft;
        }
      });

      isSyncing = false;
    };

    containers.forEach(el => el.addEventListener('scroll', handleScroll));

    return () => {
      containers.forEach(el => el.removeEventListener('scroll', handleScroll));
    };
  }, [selector, ...deps]);
}

export const hiringStatisticsDataMapper = (resp: ReportingSectionResponse[]): HiringStatisticsRow[] => {
  const empty = !resp.reduce(
    (s, r) =>
      s +
      r.transitions.reduce(
        (sum, t) => sum + t.periods.reduce((per, p) => per + (p.confirmed_on_transition || 0), 0),
        0
      ),
    0
  );

  return empty
    ? []
    : resp.map(({ block_name, block_total, transitions }, id) => {
        const total_by_month = transitions
          .filter(item =>
            block_name === 'Hired' ? true : ['During the period', 'Left from the previous period'].includes(item.name)
          )
          .flatMap(item => item.periods)
          .reduce<Record<string, number>>((acc, { month, year, confirmed_on_transition = 0 }) => {
            const key = `${month}${year}`;
            acc[key] = (acc[key] || 0) + confirmed_on_transition;
            return acc;
          }, {});
        return {
          uuid: id.toString(),
          name: block_name,
          total: block_total || '-',
          ...total_by_month,
          transitions:
            block_name !== 'Hired' &&
            transitions
              .filter(({ name }) => !(REJECTED_COLLAPSED as readonly string[]).includes(name))
              .map(({ name, transition_total, periods }, t_id) => ({
                uuid: `${id}_${t_id}`,
                name,
                total:
                  HIDE_PERCENTAGE.includes(name) || !transition_total
                    ? transition_total || '-'
                    : {
                        tooltip: `Ratio value = Total ${name} / Total ${block_name} x 100`,
                        value: transition_total,
                        percentage: ` (${((transition_total / (block_total || 1)) * 100).toFixed(2)}%)`,
                      },
                ...periods.reduce<Record<string, ReportingCellValue>>((acc, t) => {
                  acc[t.month + t.year] =
                    HIDE_PERCENTAGE.includes(name) || !t.confirmed_on_transition
                      ? t.confirmed_on_transition || '-'
                      : {
                          tooltip: `Ratio value = ${t.month} ${name} / ${t.month} ${block_name} x 100`,
                          value: t.confirmed_on_transition,
                          percentage: ` (${(
                            (t.confirmed_on_transition / total_by_month[`${t.month}${t.year}`]) *
                            100
                          ).toFixed(2)}%)`,
                        };
                  return acc;
                }, {}),
                declined: COLLAPSED_BY_NAME[name]
                  ? transitions
                      .filter(t => COLLAPSED_BY_NAME[name].includes(t.name))
                      .filter(t => (t.name === 'Blacklisted candidate' ? !!t.transition_total : true))
                      .map((d, d_id) => ({
                        uuid: `${id}_${t_id}_${d_id}`,
                        name: d.name,
                        total: d.transition_total || '-',
                        ...d.periods.reduce<Record<string, string | number>>((acc, t) => {
                          acc[t.month + t.year] = t.confirmed_on_transition || '-';
                          return acc;
                        }, {}),
                      }))
                  : [],
              })),
        };
      });
};

const renderRatioCell = (data: ReportingCellValue) =>
  typeof data === 'object' && data !== null && 'value' in data ? (
    <Tooltip label={(data as RatioCell).tooltip}>
      {(data as RatioCell).value}
      {(data as RatioCell).percentage}
    </Tooltip>
  ) : (
    data
  );

const renderSuccessCell = (data: ReportingCellValue) =>
  typeof data === 'object' && data !== null && 'tooltip' in data && 'text' in data ? (
    <Tooltip label={(data as SuccessRateCell).tooltip}>{(data as SuccessRateCell).text}</Tooltip>
  ) : (
    data
  );

export const hiringStatisticsColumnsMapper = (periods: ReportingPeriod[]): TableColumnType[] => [
  {
    title: 'Total',
    key: 'total',
    dataIndex: 'total',
    width: 200,
    render: data => renderRatioCell(data as ReportingCellValue),
  },
  ...periods.map(el => ({
    title: el.month,
    key: el.month + el.year,
    dataIndex: el.month + el.year,
    width: 200,
    render: (data: unknown) => renderRatioCell((data as ReportingCellValue) || '-'),
  })),
];

export const successRateColumnsMapper = (periods: ReportingPeriod[]): TableColumnType[] => [
  COLUMNS[0],
  {
    title: 'Total',
    dataIndex: 'total',
    key: 'total',
    width: 200,
    render: data => renderSuccessCell(data as ReportingCellValue),
  },
  ...periods.map(el => ({
    title: el.month,
    key: el.month + el.year,
    dataIndex: el.month + el.year,
    width: 200,
    render: (data: unknown) => renderSuccessCell(data as ReportingCellValue),
  })),
];

export const successRateDataMapper = (resp: ReportingSectionResponse[]): SuccessRateRow[] =>
  resp
    .filter(el => el.success_rate)
    .map(el => {
      const data: Record<string, SuccessRateCell> = {};

      el.success_rate!.periods.forEach(p => {
        data[p.month + p.year] = {
          text: `${p.rate}%`,
          tooltip: `Success rate  = ${p.month} Job offers Accepted / ${p.month} ${el.block_name} X 100`,
        };
      });
      return {
        id: el.block_name,
        name: el.block_name,
        total: {
          text: `${el.success_rate!.average_success_rate}%`,
          tooltip: `Success rate  = Total Job offers Accepted / Total ${el.block_name} X 100`,
        },
        ...data,
      };
    });
